import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "src/page/store";
import { useUserDisplayName } from "src/utils/useUserDisplayName";
import { resolveUserNamesAction } from "src/page/userNamesSlice";
import { EmptyCell } from "src/components/UI/transactionCells";

// The Users-table Approver columns show the effective reviewer for each user —
// level 1 is "Approver", level 2 the optional second approver (verifier):
// their OWN approver override (from their preferences, fetched per-page into the
// userNames.approversCache) when set, otherwise the organisation's UNIVERSAL
// default approver. Names are resolved via the shared name cache.
export default function ApproverCell({ userId, level = 1 }: { userId?: string; level?: 1 | 2 }) {
  const dispatch = useAppDispatch();
  const ownApprovers = useAppSelector((s) => {
    if (!userId) return undefined;

    return level === 2 ? s.userNames.verifiersCache[userId] : s.userNames.approversCache[userId];
  });
  const defaultApprovers = useAppSelector((s) => {
    const prefs = s.auth.orgDetail?.organisation?.preferences;

    return level === 2 ? prefs?.default_verifiers : prefs?.default_approvers;
  });
  const getName = useUserDisplayName();

  // Own override wins; fall back to the org universal approver when the user has
  // none of their own (or their overrides haven't loaded yet).
  const effectiveIds = ownApprovers && ownApprovers.length ? ownApprovers : defaultApprovers ?? [];
  const effectiveKey = effectiveIds.join(",");

  useEffect(() => {
    if (effectiveIds.length) dispatch(resolveUserNamesAction(effectiveIds));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, effectiveKey]);

  if (!effectiveIds.length) return <EmptyCell />;

  return <span>{effectiveIds.map((id) => getName(id)).join(", ")}</span>;
}
